import React, { FC, useEffect, useState } from 'react';
import { Checkbox } from 'antd';
import {
    CheckboxItem,
    CheckboxLabel,
    FooterRow,
    ModalBody,
    ModalDescription,
    ModalDivider,
    NotificationModalContainer,
    NotificationModalSpin,
} from './NotificationModal.styles';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { CloseIcon, PrimaryButton } from '../../../../../styles/common.styles';
import ModalTitle from '../../../../../components/common/modalTitle/ModalTitle';
import {
    getLiveGuidelineNotificationPreferencesAction,
    updateLiveGuidelineNotificationPreferenceAction,
} from '../../../../../redux/actions/liveGuidelineActions';
import { alertService } from '../../../../../services';

interface NotificationSettings {
    displayOnDashboard: boolean;
    emailEachTime: boolean;
    emailDigest: boolean;
}

interface Props {
    isOpen: boolean;
    liveGuidelineId?: number;
    handleCancel: () => void;
    onSuccess?: () => void;
}

const NotificationModal: FC<Props> = ({
    isOpen,
    handleCancel,
    liveGuidelineId,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const { liveGuidelineReducer } = useAppSelector((state) => state);

    const [settings, setSettings] = useState<NotificationSettings>({
        displayOnDashboard: false,
        emailEachTime: false,
        emailDigest: false,
    });

    useEffect(() => {
        if (!isOpen || !liveGuidelineId) {
            return;
        }

        void (async () => {
            try {
                const response = await dispatch(
                    getLiveGuidelineNotificationPreferencesAction(),
                ).unwrap();
                const preference = response?.data.find(
                    (item) => item.liveGuidelineId === liveGuidelineId,
                );

                if (preference) {
                    setSettings({
                        displayOnDashboard: preference.displayOnDashboard,
                        emailEachTime: preference.sendEmailMeEachTime,
                        emailDigest: preference.sendEmailDigest,
                    });
                }
            } catch (error) {
                alertService.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to load notification preferences',
                );
            }
        })();
    }, [isOpen, liveGuidelineId]);

    const toggle = (key: keyof NotificationSettings) => {
        setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const onSubmit = async () => {
        if (!liveGuidelineId) {
            return;
        }

        try {
            await dispatch(
                updateLiveGuidelineNotificationPreferenceAction({
                    liveGuidelineId,
                    preference: {
                        displayOnDashboard: settings.displayOnDashboard,
                        sendEmailMeEachTime: settings.emailEachTime,
                        sendEmailDigest: settings.emailDigest,
                    },
                }),
            ).unwrap();

            onSuccess?.();
        } catch (error) {
            alertService.error(
                error instanceof Error ? error.message : 'Failed to save notification preferences',
            );
        }
    };

    return (
        <NotificationModalContainer
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
            centered
            closeIcon={<CloseIcon />}
        >
            <NotificationModalSpin size="large" spinning={liveGuidelineReducer.pending}>
                <ModalTitle
                    title="Manage subscription notifications"
                    icon="guidline-lightning.svg"
                />
                <ModalBody>
                    <ModalDescription>
                        A record of Live Guideline notifications is always available from each Live
                        Guideline's Notifications tab.
                    </ModalDescription>

                    <CheckboxItem>
                        <Checkbox
                            checked={settings.displayOnDashboard}
                            onChange={() => toggle('displayOnDashboard')}
                        />
                        <CheckboxLabel>Display on Dashboard</CheckboxLabel>
                    </CheckboxItem>

                    <CheckboxItem>
                        <Checkbox
                            checked={settings.emailEachTime}
                            onChange={() => toggle('emailEachTime')}
                        />
                        <CheckboxLabel>Email me each time</CheckboxLabel>
                    </CheckboxItem>

                    <CheckboxItem>
                        <Checkbox
                            checked={settings.emailDigest}
                            onChange={() => toggle('emailDigest')}
                        />
                        <CheckboxLabel>Email digest (sent weekly)</CheckboxLabel>
                    </CheckboxItem>
                </ModalBody>

                <ModalDivider />

                <FooterRow>
                    <PrimaryButton onClick={onSubmit}>Set Live Guideline criteria</PrimaryButton>
                </FooterRow>
            </NotificationModalSpin>
        </NotificationModalContainer>
    );
};

export default NotificationModal;
